import { z } from 'zod';
import {
  AuditSchema,
  DateOnlySchema,
  IdSchema,
  InstantSchema,
  VisibilitySchema,
} from './common.ts';

/**
 * Weather as it was on the day, not as it is now.
 *
 * Recorded at save time and then left alone. A daily log is evidence — of a
 * day lost to rain, of why a pour was moved — so re-fetching it later would
 * quietly rewrite the record. The fetch is a function's job (P3-07); this is
 * only the shape it fills.
 *
 * Fahrenheit and inches, because the product is sold in the United States
 * and `SPEC.md` scopes it there. A locale setting would mean storing the
 * provider's units and converting on read; that is a later decision, and
 * recording it here is what makes it one rather than a surprise.
 */
export const WeatherSchema = z.object({
  /** "Partly cloudy with heavy rain", as the provider words it. */
  summary: z.string().max(200),
  /** Provider-independent token the UI maps to an icon. */
  icon: z.string().max(60).optional(),
  highF: z.number().int().optional(),
  lowF: z.number().int().optional(),
  windMph: z.number().int().nonnegative().optional(),
  humidityPct: z.number().int().min(0).max(100).optional(),
  /** Hundredths of an inch, so no float ever rounds a rain total. */
  precipHundredthsIn: z.number().int().nonnegative().optional(),
  fetchedAt: InstantSchema,
});
export type Weather = z.infer<typeof WeatherSchema>;

export const DEFAULT_LOG_TITLE = 'Daily Report';

/**
 * One day's log on one job.
 *
 * Job-scoped in the path rather than carrying a `jobId` field, which is the
 * opposite of `files` and deliberately so: a log belongs to exactly one job
 * and is never shared between them, and the path is the only thing a
 * Firestore `list` rule can gate assignment on without a document read per
 * returned row. Files pay that cost because a company template and a job
 * document want one collection; logs have no such case.
 *
 * Attachments are not stored here. A file points at the records it is
 * attached to (`File.linkedRecords`), and holding the list on both sides
 * would mean two writes that can disagree — the version a reader sees then
 * depends on which one they read.
 */
export const DailyLogSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  date: DateOnlySchema,
  title: z.string().trim().min(1).max(200).default(DEFAULT_LOG_TITLE),
  /**
   * The body of the log.
   *
   * Plain text for now. The feature doc asks for rich text, which is an
   * editor and a sanitiser rather than a field type — see P3-06d. Storing
   * markup before anything sanitises it would be storing an XSS payload
   * with a view to rendering it to subs and homeowners later.
   */
  notes: z.string().max(20_000).default(''),
  tagIds: z.array(IdSchema).default([]),
  /**
   * Answers to the company's own questions, keyed by field id.
   *
   * Values are always a list, even for a single answer: "Who was on site"
   * is a multi-pick and a one-answer field is the same shape with one
   * entry, which keeps the reader from having to handle both.
   */
  customFields: z.record(z.string().min(1), z.array(z.string())).default({}),
  visibility: VisibilitySchema,
  /** People to tell, over and above whoever the audience reaches. */
  notifyUids: z.array(IdSchema).default([]),
  weather: WeatherSchema.optional(),
  /** Free text beside the fetched conditions: "rained out after lunch". */
  weatherNotes: z.string().max(2_000).optional(),
});
export type DailyLog = z.infer<typeof DailyLogSchema>;

/** Whether anybody outside the company can see this log. */
export function reachesExternal(log: Pick<DailyLog, 'visibility'>): boolean {
  if (log.visibility.private) return false;
  return log.visibility.subs || log.visibility.owner;
}
